import { z } from "zod";

import {
  boundedTextSchema,
  hasUniqueValues,
  kebabCaseIdentifierSchema,
  localOggAudioReferenceSchema
} from "@/domain/schemas";

export const scenarioNarrationSchema = z.strictObject({
  text: boundedTextSchema(500),
  audio: localOggAudioReferenceSchema.optional()
});

export type ScenarioNarration = z.infer<typeof scenarioNarrationSchema>;

export const scenarioChoiceSafetySchema = z.enum(["safe", "unsafe"]);

export type ScenarioChoiceSafety = z.infer<
  typeof scenarioChoiceSafetySchema
>;

export const scenarioChoiceSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  text: boundedTextSchema(160),
  audio: localOggAudioReferenceSchema.optional(),
  safety: scenarioChoiceSafetySchema,
  supportiveFeedback: scenarioNarrationSchema
});

export type ScenarioChoice = z.infer<typeof scenarioChoiceSchema>;

const scenarioChoicesSchema = z
  .array(scenarioChoiceSchema)
  .min(2)
  .max(4)
  .refine((choices) => hasUniqueValues(choices.map(({ id }) => id)), {
    message: "Scenario choice identifiers must be unique"
  })
  .refine((choices) => hasUniqueValues(choices.map(({ text }) => text)), {
    message: "Scenario choice text must be unique"
  });

const canonicalScenarioPayloadSchema = z
  .strictObject({
    narration: scenarioNarrationSchema,
    choices: scenarioChoicesSchema,
    bestChoiceId: kebabCaseIdentifierSchema,
    followUp: scenarioNarrationSchema.optional()
  })
  .superRefine((payload, context) => {
    const bestChoiceIndex = payload.choices.findIndex(
      ({ id }) => id === payload.bestChoiceId
    );

    if (bestChoiceIndex === -1) {
      context.addIssue({
        code: "custom",
        path: ["bestChoiceId"],
        message: "Best choice must reference a scenario choice"
      });
      return;
    }

    if (payload.choices[bestChoiceIndex]?.safety !== "safe") {
      context.addIssue({
        code: "custom",
        path: ["choices", bestChoiceIndex, "safety"],
        message: "The best scenario choice must be safe"
      });
    }
  });

export const FRIENDSHIP_SCENARIO_TOPICS = [
  "sharing",
  "boundaries",
  "emotions",
  "conflict-repair",
  "inclusion",
  "help-seeking"
] as const;

const friendshipChoiceSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  label: boundedTextSchema(160),
  audio: localOggAudioReferenceSchema.optional(),
  feedback: boundedTextSchema(500),
  feedbackAudio: localOggAudioReferenceSchema.optional()
});

const friendshipScenarioPayloadSchema = z
  .strictObject({
    topic: z.enum(FRIENDSHIP_SCENARIO_TOPICS),
    sceneNarration: boundedTextSchema(500),
    question: boundedTextSchema(300),
    narrationAudio: localOggAudioReferenceSchema.optional(),
    choices: z
      .array(friendshipChoiceSchema)
      .min(2)
      .max(4)
      .refine((choices) => hasUniqueValues(choices.map(({ id }) => id)), {
        message: "Scenario choice identifiers must be unique"
      })
      .refine(
        (choices) => hasUniqueValues(choices.map(({ label }) => label)),
        { message: "Scenario choice labels must be unique" }
      ),
    bestChoiceId: kebabCaseIdentifierSchema,
    followUp: boundedTextSchema(500).optional(),
    followUpAudio: localOggAudioReferenceSchema.optional()
  })
  .superRefine((payload, context) => {
    if (!payload.choices.some(({ id }) => id === payload.bestChoiceId)) {
      context.addIssue({
        code: "custom",
        path: ["bestChoiceId"],
        message: "Best choice must reference a scenario choice"
      });
    }

    if (payload.followUp === undefined && payload.followUpAudio !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["followUpAudio"],
        message: "Follow-up audio requires follow-up text"
      });
    }
  });

type CanonicalScenarioPayload = z.infer<
  typeof canonicalScenarioPayloadSchema
>;
type FriendshipScenarioPayload = z.infer<
  typeof friendshipScenarioPayloadSchema
>;

function normalizeFriendshipScenario(
  payload: FriendshipScenarioPayload
): CanonicalScenarioPayload {
  return {
    narration: {
      text: payload.sceneNarration + " " + payload.question,
      ...(payload.narrationAudio === undefined
        ? {}
        : { audio: payload.narrationAudio })
    },
    choices: payload.choices.map((choice) => ({
      id: choice.id,
      text: choice.label,
      ...(choice.audio === undefined ? {} : { audio: choice.audio }),
      safety: "safe" as const,
      supportiveFeedback: {
        text: choice.feedback,
        ...(choice.feedbackAudio === undefined
          ? {}
          : { audio: choice.feedbackAudio })
      }
    })),
    bestChoiceId: payload.bestChoiceId,
    ...(payload.followUp === undefined
      ? {}
      : {
          followUp: {
            text: payload.followUp,
            ...(payload.followUpAudio === undefined
              ? {}
              : { audio: payload.followUpAudio })
          }
        })
  };
}

export const scenarioPayloadSchema = z
  .union([canonicalScenarioPayloadSchema, friendshipScenarioPayloadSchema])
  .transform((payload): CanonicalScenarioPayload =>
    "narration" in payload ? payload : normalizeFriendshipScenario(payload)
  )
  .pipe(canonicalScenarioPayloadSchema);

export type ScenarioPayload = z.infer<typeof scenarioPayloadSchema>;
